import {
  byteLen,
  type DoczFragment,
  type FragmentRun,
  fragmentStyleMarks,
  fragmentToBlock,
  type MirrorBlock,
  sanitizeCommentHtml,
  type StructuredBlock,
} from "@norkron/web-docz";
import {
  type ActivatableRange,
  fragmentPageConfig,
  MIN_COLUMN_WIDTH_PT,
  orderActivatable,
} from "@norkron/web-page-layout";
import type { PageConfig } from "@norkron/web-page-layout";
import type { MentionRef } from "../../api/types";

/**
 * `commentSurfaceModel` — the DOM-free, wasm-free half of the CANVAS comment
 * body (owner decision **D-E**, `canvas-unification.md`: *no DOM renders any
 * docz IR anywhere*, comment bodies included, because a `docz-fragment` body IS
 * real docz IR lowered through the SAME `html_writer` as the document).
 *
 * ## What lives here and why it is separate
 *
 * A `<canvas>` has no `<a>`, no text node, no `:hover` and no accessibility
 * tree. Three capabilities the DOM gave away for free therefore have to be
 * REBUILT, and every one of them reduces to the SAME question: *which UTF-8 byte
 * range of the fragment is this?*
 *
 *   · a **link** is the byte range of the runs carrying a `link` mark;
 *   · a **mention chip** is the byte range of a literal `@<recorded name>`;
 *   · the **a11y projection** is the fragment's HTML plus a byte oracle.
 *
 * That coordinate — `(blockOrd, byteOffset)` — is the one the geometry engine,
 * `EditorCore.rangeRects` and `el.comment_anchors` already speak, so a painted
 * band, a hit test and a comment anchor cannot disagree. Byte offsets are stable
 * across relayout; PIXELS ARE NOT, which is exactly why the durable half is
 * offsets and the rects are always re-measured.
 *
 * Keeping it out of the React component is not tidiness: happy-dom has no
 * Canvas 2D context, so a mounted surface can never be reached headlessly
 * (`renderBand` throws `2D context unavailable`). Everything asserted about
 * links, mentions, page geometry and the sanitizer boundary is asserted HERE, in
 * bun, against real fixtures.
 *
 * ## RULE ZERO
 *
 * Nothing here re-implements a lib. The block projection is web-docz's own
 * `fragmentToBlock`; the link ranges are its `fragmentStyleMarks` (the same
 * byte accounting the transform verbs use); the column arithmetic is
 * web-page-layout's `columnWidthPtForCss`; the sanitizer is web-docz's
 * `sanitizeCommentHtml`. What is genuinely elaborate's is the MENTION policy
 * (`MentionRef` is this product's type) and the merge into one keyed hit set.
 */

// ─── the block the layout pass encodes ───────────────────────────────────────

/**
 * The stored comment fragment as a real, mark-carrying docz `StructuredBlock` —
 * the unit `createStructuredDoczHandle` wraps and `makeStructuredEncodeBlocks`
 * hands to `pl_layout`.
 *
 * Delegated to web-docz rather than spelled out here: that package is the ONE
 * place the fragment→block shape is decided, so a heading fragment keeps its
 * level and a future block kind does not need a second mapping to learn about.
 *
 * ## ⛔ Why this is `fragmentToBlock` and NOT `fragmentIntoBlock`
 *
 * It used to call `fragmentIntoBlock({ id, kind: 'paragraph', runs: [] }, frag)`
 * — a THROWAWAY empty-paragraph seed whose only purpose was to land on that
 * function's run-bearing-leaf tier. When `fragmentIntoBlock` grew its refusal
 * (`StructuredBlock | null`, so a multi-item list can no longer be silently
 * shredded into a `<p>`), this line went RED under `tsc` — and the tempting fixes
 * were all wrong:
 *
 *   · `!` / `as StructuredBlock` — asserts away a branch, which is exactly how a
 *     real refusal later gets waved through at the site meant to catch it;
 *   · `?? somethingEmpty` — invents a comment body the author never wrote;
 *   · `| null` on the return — pushes an impossible case onto every caller and
 *     onto the surface that has to paint SOMETHING.
 *
 * The honest reading is that **`null` was never reachable here**: the seed is a
 * `paragraph` LITERAL, constructed one line up, so the leaf tier is the only tier
 * that can run and it always returns a block. The bug was the SPELLING — a
 * write-back API used as a constructor — not a missing check. `fragmentToBlock`
 * is that constructor with an honest, TOTAL type, so the impossibility is now a
 * property of the FUNCTION rather than of this call site's seed. A refusal that
 * cannot occur must not be checked; a check that can never fail is
 * indistinguishable from one that has been silenced.
 *
 * The refusal itself is unharmed: `fragmentIntoBlock` still refuses everywhere a
 * real block is being rewritten in place. A comment body is not a rewrite — there
 * is no block yet.
 */
export function commentBlockFromFragment(
  frag: DoczFragment,
  id: string,
): StructuredBlock {
  return fragmentToBlock(frag, id);
}

/**
 * The same runs, with a DEFAULT INK for every run that carries no colour of its
 * own — the ink half of the paper/ink pair (`lib/canvasCommentColors`: the paper
 * is `FRAGMENT_PAPER`, i.e. none, and the ink is `surfaceInk`'s).
 *
 * ## Why this is a mark and not an option (today)
 *
 * `@norkron/web-page-layout`'s shipped `dist` accepts `paper` but not yet `ink`
 * (the option exists in that package's working tree; it is not in the built
 * artefact this app resolves). Its painter reads the ink per run —
 * `run.hasColor ? colorCss(run) : <black>` — so the one way to move the default
 * from the outside TODAY is to make `hasColor` true, and docz already has the
 * mark for that: `{ type: 'color' }` is a first-class `MarkType`, not a shim.
 *
 * ⇒ When `CanvasSurfaceOptions.ink` reaches the built lib, DELETE this function
 * and pass `ink` instead. The behaviour is identical; the option is the honest
 * spelling. This is the only reason it exists.
 *
 * ## ⛔ What it must never touch
 *
 * The STORED fragment. This runs on the block handed to the layout engine and
 * nothing else: the accessible mirror is rendered from the original fragment
 * (`CommentCanvasBody` → `renderFragmentToHtml`), so no theme colour is written
 * into the markup a screen reader reads, and nothing colour-shaped is ever
 * persisted. A run that DOES carry an author colour keeps it — a deliberate
 * colour in a comment is content, and overriding it would be a data loss the
 * reader could not detect.
 */
export function withDefaultInk(
  runs: readonly FragmentRun[],
  ink: string,
): readonly FragmentRun[] {
  return runs.map((run) =>
    run.marks.some((m) => m.type === "color")
      ? run
      : { ...run, marks: [...run.marks, { type: "color" as const, color: ink }] },
  );
}

/**
 * {@link withDefaultInk} over a whole block, for the block kinds that HAVE runs.
 *
 * A `StructuredBlock` is a discriminated union and only some arms carry `runs`
 * (a `list` carries `items`, a `table` carries rows). Anything else is returned
 * UNCHANGED rather than reshaped — a comment fragment is a paragraph or a
 * heading, and quietly inventing a `runs` field on a kind that has none would be
 * a worse bug than the one this fixes.
 */
export function blockWithDefaultInk(
  block: StructuredBlock,
  ink: string,
): StructuredBlock {
  if (!("runs" in block)) return block;
  return { ...block, runs: withDefaultInk(block.runs, ink) } as StructuredBlock;
}

// ─── the interactive targets a canvas has to rebuild ─────────────────────────

/** A clickable link recovered from a `link` mark's byte range. */
export interface CommentLinkTarget {
  readonly kind: "link";
  /** Stable identity for the painted-band cache. */
  readonly key: string;
  readonly start: number;
  readonly end: number;
  readonly href: string;
}

/** A mention chip recovered from a literal `@<recorded name>` byte range. */
export interface CommentMentionTarget {
  readonly kind: "mention";
  readonly key: string;
  readonly start: number;
  readonly end: number;
  readonly userId: number;
  readonly name: string;
}

export type CommentTarget = CommentLinkTarget | CommentMentionTarget;

/**
 * Every `link` mark in the fragment, as a byte range + its href.
 *
 * `fragmentStyleMarks` is upstream byte accounting that already coalesces a mark
 * across adjacent runs and keys by VALUE, so two different hrefs stay two
 * targets while one href split across a bold boundary stays one.
 *
 * The href needs no re-validation here: `decodeFragment` → `fragmentFromJson`
 * drops any `link` mark failing `isSafeHref` at the IR boundary (FR-B1), so a
 * `javascript:` link never reaches a target and degrades to plain text.
 */
export function commentLinkTargets(
  frag: DoczFragment,
): readonly CommentLinkTarget[] {
  const out: CommentLinkTarget[] = [];
  for (const range of fragmentStyleMarks(frag)) {
    if (range.mark.type !== "link") continue;
    out.push({
      kind: "link",
      key: `link:${range.start}:${range.end}`,
      start: range.start,
      end: range.end,
      href: range.mark.href,
    });
  }
  return out;
}

/** Runs a mention scan must SKIP — the canvas twin of `chipifyMentions`'s
 *  `<a>` / `<code>` subtree skip. A chip inside a link would steal its click. */
function skipsMentions(run: FragmentRun): boolean {
  return run.marks.some((m) => m.type === "link" || m.type === "code");
}

/**
 * Every literal `@<recorded name>` in the fragment, as a byte range.
 *
 * Scanned PER RUN, which is exactly what the DOM path did (it walked text
 * nodes): a name split across a formatting boundary was never chipped there
 * either, so the canvas and the DOM agree on what a chip is. Names are matched
 * LONGEST FIRST so `@Ada Lovelace` is not truncated to `@Ada`.
 */
export function commentMentionTargets(
  frag: DoczFragment,
  mentions: readonly MentionRef[],
): readonly CommentMentionTarget[] {
  if (mentions.length === 0) return [];
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const out: CommentMentionTarget[] = [];
  let base = 0;
  for (const run of frag.runs) {
    const text = run.text;
    if (skipsMentions(run)) {
      base += byteLen(text);
      continue;
    }
    let pos = 0;
    while (pos < text.length) {
      const at = text.indexOf("@", pos);
      if (at === -1) break;
      const hit = names.find((m) => text.startsWith(`@${m.name}`, at));
      if (!hit) {
        pos = at + 1;
        continue;
      }
      const chars = 1 + hit.name.length;
      const start = base + byteLen(text.slice(0, at));
      const end = start + byteLen(text.slice(at, at + chars));
      out.push({
        kind: "mention",
        key: `mention:${start}:${end}:${hit.user_id}`,
        start,
        end,
        userId: hit.user_id,
        name: hit.name,
      });
      pos = at + chars;
    }
    base += byteLen(text);
  }
  return out;
}

/** Links + mention chips as ONE keyed set — what the painted-band plan and the
 *  pointer hit test both run against, so they can never diverge. */
export function commentInteractiveTargets(
  frag: DoczFragment,
  mentions: readonly MentionRef[],
): readonly CommentTarget[] {
  return [...commentLinkTargets(frag), ...commentMentionTargets(frag, mentions)];
}

// ─── the hit test ────────────────────────────────────────────────────────────

/** A page-space point, as the engine's `pagePointAt` reports it. */
export interface SurfacePoint {
  readonly pageIndex: number;
  readonly xPt: number;
  readonly yPt: number;
}

/** A page-space band — structurally the engine's `PageRect`. Kept local so this
 *  module never needs the engine to be TESTABLE. */
export interface SurfaceBand extends SurfacePoint {
  readonly wPt: number;
  readonly hPt: number;
}

function contains(r: SurfaceBand, p: SurfacePoint): boolean {
  // HALF-OPEN on both axes, so two adjacent bands never both claim a boundary.
  return (
    r.pageIndex === p.pageIndex &&
    p.xPt >= r.xPt &&
    p.xPt < r.xPt + r.wPt &&
    p.yPt >= r.yPt &&
    p.yPt < r.yPt + r.hPt
  );
}

/**
 * Which target's PAINTED band is under `point`, or `null` for anywhere else.
 *
 * GEOMETRY, NOT A BYTE OFFSET — deliberately. The engine also exposes
 * `positionAt`, but that is the CARET hit test and it SNAPS: a press in a margin
 * or past the last line is rescued onto the nearest line, which is right for
 * "put the caret here" and wrong for "did the reader click ON this link". A
 * click in blank space would open a link nowhere near the pointer. Testing the
 * rects that were actually painted makes "what the reader sees is what they hit"
 * true by construction.
 *
 * INNERMOST WINS: among overlapping bands the SHORTEST byte range takes the hit.
 * A mention chip inside a linked phrase would otherwise be permanently
 * unclickable, while the enclosing link stays reachable elsewhere.
 */
export function targetAtPoint(
  targets: readonly CommentTarget[],
  bandsByKey: ReadonlyMap<string, readonly SurfaceBand[]>,
  point: SurfacePoint | null,
): CommentTarget | null {
  if (!point) return null;
  let best: CommentTarget | null = null;
  for (const t of targets) {
    const bands = bandsByKey.get(t.key);
    if (!bands?.some((b) => contains(b, point))) continue;
    if (best === null || t.end - t.start <= best.end - best.start) best = t;
  }
  return best;
}

// ─── the page the comment lays out on ────────────────────────────────────────

/**
 * The narrowest card width a comment column is laid out at. Below `2in` a
 * justified measure degenerates into rivers and the wasm wrap budget starves
 * (`MIN_COLUMN_WIDTH_PT`), so this is that same floor expressed in CSS px —
 * named because it is ALSO the honest answer for an unmeasurable card.
 */
export const COMMENT_SURFACE_MIN_WIDTH_CSS = Math.ceil(
  (MIN_COLUMN_WIDTH_PT * 96) / 72,
);

/**
 * The `PageConfig` a comment body lays out under: ONE continuous column, zero
 * margins, reflowed to the CARD.
 *
 * ## ⛔ NO `inherit` — and that ABSENCE is the load-bearing part
 *
 * The plumbing (`continuous: true`, four zero margins, `pageWidthPt` from
 * `columnWidthPtForCss` with a stated fallback) is upstream in
 * `fragmentPageConfig`, because the anchored quote needs exactly the same three
 * things and a second copy of them was a duplication defect.
 *
 * What is NOT shared is whether the fragment inherits the DOCUMENT's plate. A
 * QUOTE is the document — a clipping of the page — so `quotePageConfig` spreads
 * `CANVAS_EDIT_PAGE_CONFIG`. **A COMMENT IS NOT THE DOCUMENT.** Laying it out on
 * the document's page-master would apply a 595pt sheet's geometry (99/72pt
 * margins, the plate's heading rhythm, its `pageHeightPt`) to something that is
 * not a page at all. So this builds from NOTHING and adds only what a comment
 * needs. `fragmentPageConfig` has no `inherit` default precisely so that this
 * call site cannot acquire one by accident.
 *
 * `glyphGranular: true` rides in `overrides` rather than being assumed from the
 * engine default, and that is LOAD-BEARING: per-glyph advances (with the
 * Knuth–Plass wrap that carries per-line provenance) are the only reason a link
 * or a mention has a byte range to hit-test at all — i.e. the only reason either
 * is clickable OR keyboard-reachable. The cost (a comment paints justified even
 * when the author's alignment was ragged) is real and closes only with the pdfz
 * provenance FR.
 *
 * ## ⛔ `measuredGreedy: true` — THE LINE-BREAKING BUG, and why it lived here
 *
 * Owner-reported 2026-07-30: *"comments wrap in bizarre places, mid-width — is
 * there some internal canvas width set that's unrelated to the current 596px
 * width?"* There is, and it is not a width: it is a CHARACTER COUNT standing in
 * for one.
 *
 * pdfz's default ragged wrap budget is `.measured` — `cols = floor(inner_w_pt /
 * shaper.measure(font, size, "M"))` — and the per-line fit test then counts
 * CODEPOINTS against `cols`. `flex.zig`'s own words on the flag that turns that
 * off: *"required for proportional embedded faces, where 'M' is far wider than
 * the average glyph so the count budget wraps ragged text to a fraction of its
 * column."* Alexandria is proportional, so every comment line broke at ~`column /
 * advance("M")` characters no matter how much room was left.
 *
 * MEASURED on production `a9c50c0`, doc 11, `getImageData` over the painted
 * canvases at a 581 px host: first-line ink ended at **390 / 304 / 345 CSS px** in
 * a **581 px** sheet — 52–67 % of the measure, with 191–277 px of blank paper to
 * the right. Those are the owner's three examples, to the pixel.
 *
 * The WIDTH was never wrong: the same probe read 581 / 509 / 473 px hosts for a
 * root note and its depth-2 / depth-3 replies, i.e. the column already reflows
 * per instance. Only the budget was.
 *
 * ⇒ `measuredGreedy: true` makes the fit test use the REAL shaper advance
 * (`wrapGreedyMeasured`). It is the SAME value {@link BODY_PAGE_CONFIG} has
 * carried since the branded preview closed the identical defect for the document
 * body ("without it the canvas body wraps on the `measure("M")` count budget"),
 * and the quote box inherits it through `CANVAS_EDIT_PAGE_CONFIG`. A comment body
 * inherits NOTHING (see above), so it was the one fragment surface left on the
 * count budget — the absence of `inherit` is why the bug could hide here alone.
 *
 * ⛔ It is a MEASUREMENT, not a tuned constant: there is no number here to nudge.
 * Widen the card and the extra room is used; nest a reply and the narrower column
 * is respected. Both are asserted at three widths in `commentSurfaceModel.test.ts`
 * and painted in `comment-canvas-proof`.
 */
export function commentPageConfig(widthCss: number, zoom = 1): PageConfig {
  return fragmentPageConfig({
    widthCss,
    zoom,
    // ⛔ NO `inherit`. A comment is not the document — see the note above.
    fallbackWidthCss: COMMENT_SURFACE_MIN_WIDTH_CSS,
    fallbackWidthPt: MIN_COLUMN_WIDTH_PT,
    overrides: { glyphGranular: true, measuredGreedy: true },
  });
}

// ─── the keyboard route to a painted target ──────────────────────────────────

/**
 * The interactive targets as the surface's ACTIVATABLE BANDS.
 *
 * ## Why this exists: the targets were pointer-only
 *
 * A painted link and a painted mention chip are hit-tested with `pagePointAt` +
 * containment against the rects that were painted. That is a POINTER affordance
 * and nothing else. A screen-reader user still reaches the link, because
 * `DocumentMirror` projects the real `<a>`; a SIGHTED KEYBOARD-ONLY user had no
 * route to it — no tab stop of their own, no key, no command. **WCAG 2.1.1
 * (Keyboard, A)** unmet, and **2.4.7 (Focus Visible, AA)** with it.
 *
 * The coordinate needs no translation, which is the whole reason this is three
 * lines: an `ActivatableRange` IS `(blockOrd, start, end)` plus a key, and a
 * `CommentTarget` already carries the byte range and the key it uses for its
 * painted-band cache. Block ord is always `0` — a comment fragment is one block.
 *
 * Ordered upstream by `orderActivatable` so the traversal is DOCUMENT order
 * (WCAG 2.4.3) rather than discovery order: `commentInteractiveTargets`
 * concatenates links-from-marks with mentions-from-a-text-scan, which is neither.
 * Ties on `start` go INNERMOST FIRST, so a chip inside a linked phrase is
 * reachable — the same rule `targetAtPoint` applies to the pointer.
 */
export function commentActivatableRanges(
  targets: readonly CommentTarget[],
): readonly ActivatableRange[] {
  return orderActivatable(
    targets.map((t) => ({
      key: t.key,
      blockOrd: 0,
      start: t.start,
      end: t.end,
      // The label is what a host can put in a status line; carrying it is what
      // lets the surface's consumer say WHAT is focused rather than only that
      // something is.
      label: t.kind === "link" ? t.href : `@${t.name}`,
    })),
  );
}

// ─── the accessible projection ───────────────────────────────────────────────

/**
 * The comment body as accessible-mirror blocks, WITH the comment-body XSS
 * boundary intact.
 *
 * ## The security argument, stated rather than assumed
 *
 * Before this migration the flow was
 * `renderFragmentToHtml → sanitizeCommentHtml → dangerouslySetInnerHTML`, and
 * `sanitizeCommentHtml` (`COMMENT_PROFILE`) was THE trust boundary on
 * attacker-controllable text — a direct `POST` to the comments API bypasses
 * every client check and the backend stores the body opaquely.
 *
 * The canvas path removes the injection: glyphs are painted from the IR through
 * `pl_layout`, and no HTML is produced for the pixels at all. But the
 * accessibility projection (`DocumentMirror`) IS a DOM subtree, and it
 * sanitizes under `MIRROR_PROFILE`, which is deliberately WIDER in one
 * dimension: it admits RELATIVE hrefs, because a document's table-of-contents
 * entry (`#section-3`) needs them. A comment body is not a document — widening
 * its href policy would be a real loosening of a live control.
 *
 * So `sanitizeCommentHtml` runs FIRST, on the same string, and the mirror's own
 * pass then runs over already-sanitized markup. Sanitization is idempotent and
 * profile composition is an INTERSECTION: a relative href, a `javascript:`
 * href, a `<script>` subtree and every non-comment tag are already gone before
 * `MIRROR_PROFILE` gets a say. The boundary did not move — it changed consumer.
 *
 * `text` is the IR plain text: the ORACLE `stampByteOffsets` checks the
 * projection against before stamping any byte coordinate, so a drift REFUSES
 * instead of mis-stamping a caret.
 */
export function commentMirrorBlocks(
  renderedHtml: string,
  irText: string,
  lang?: string,
): readonly MirrorBlock[] {
  return [
    {
      ord: 0,
      html: sanitizeCommentHtml(renderedHtml),
      text: irText,
      ...(lang !== undefined ? { lang } : {}),
    },
  ];
}
